import type { ReactNode } from "react";

import {
  Calendar,
  ChevronDown,
  ChevronRight,
  Clock,
  Target,
  Users,
} from "lucide-react";

import {
  formatDiscoveryHubDate,
  type DiscoveryHubPollHubTab,
} from "./pollAccordionHelpers";
import {
  DH_POLL_CYCLE_CARD,
  DH_POLL_CYCLE_CARD_BODY,
  DH_POLL_CYCLE_CARD_HEADER,
  DH_POLL_CYCLE_CHECKBOX,
} from "../../Utils/Helper/discoveryHubUi";
import {
  formatRemainingUntilDue,
  isUrgentPollCountdown,
} from "../../Utils/Helper/sponsorEngagementUi";

export type PollGroup = {
  cycle_id: string;
  poll_name: string;
  period: string;
  due_date?: string;
  late_window_end_date?: string;
  poll1_enabled?: boolean;
  poll2_enabled?: boolean;
  target_audience?: string;
  included_in_draft_leaderboard?: boolean;
  published_to_leaderboard?: boolean;
  due_date_info?: { count?: number; reason?: string };
  completion?: {
    poll1?: { done?: number; total?: number };
    poll2?: { done?: number; total?: number };
  };
};

const MetadataItem = ({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) => (
  <div className="flex items-center gap-1">
    {icon}
    <span className="text-gray-500">{label}:</span>
    <span className="font-medium text-gray-900">{value}</span>
  </div>
);

type PollCycleCardProps = {
  group: PollGroup;
  activeHub: DiscoveryHubPollHubTab;
  sponsorCount: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  children: ReactNode;
  selectable?: boolean;
  selected?: boolean;
  onSelectChange?: (selected: boolean) => void;
  savedToDraft?: boolean;
  onExtendDueDate?: () => void;
};

const PollCycleCard = ({
  group,
  activeHub,
  sponsorCount,
  isExpanded,
  onToggleExpand,
  children,
  selectable = false,
  selected = false,
  onSelectChange,
  savedToDraft = false,
  onExtendDueDate,
}: PollCycleCardProps) => {
  const p1 = group.completion?.poll1;
  const p2 = group.completion?.poll2;
  const poll1Enabled = group.poll1_enabled !== false;
  const accountsTotal = poll1Enabled ? (p1?.total ?? 0) : (p2?.total ?? 0);
  const remainingUntilDue =
    activeHub === "ongoing" ? formatRemainingUntilDue(group.due_date) : null;
  const remainingGracePeriod =
    activeHub === "late-window"
      ? formatRemainingUntilDue(group.late_window_end_date)
      : null;
  const countdownLabel = remainingUntilDue ?? remainingGracePeriod;
  const countdownUrgent = isUrgentPollCountdown(countdownLabel);

  return (
    <div className={DH_POLL_CYCLE_CARD}>
      <div className={DH_POLL_CYCLE_CARD_HEADER}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {selectable ? (
              <input
                type="checkbox"
                className={DH_POLL_CYCLE_CHECKBOX}
                checked={selected}
                onChange={e => onSelectChange?.(e.target.checked)}
                onClick={e => e.stopPropagation()}
                aria-label={`Select ${group.poll_name} for draft leaderboard`}
              />
            ) : null}
            <button
              type="button"
              onClick={onToggleExpand}
              className="bg-transparent border-none cursor-pointer text-gray-500 transition-colors duration-200 hover:text-gray-900 shrink-0"
              aria-expanded={isExpanded}
              aria-label={isExpanded ? "Collapse poll" : "Expand poll"}
            >
              {isExpanded ? (
                <ChevronDown size={20} />
              ) : (
                <ChevronRight size={20} />
              )}
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-medium text-gray-900 mb-0">
                  {group.poll_name}
                  {countdownLabel ? (
                    <span
                      className={`text-sm font-normal ${
                        countdownUrgent
                          ? "text-red-600 font-semibold"
                          : "text-gray-500"
                      }`}
                    >
                      {" "}
                      · {countdownLabel}
                    </span>
                  ) : null}
                </h3>
                {savedToDraft ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800 border border-purple-200">
                    In draft leaderboard
                  </span>
                ) : null}
                {group.due_date_info?.count ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs bg-amber-50 text-amber-800 border border-amber-200">
                    Extended: {group.due_date_info.count} time
                    {group.due_date_info.count === 1 ? "" : "s"}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          {activeHub === "ongoing" && onExtendDueDate ? (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-sm font-medium text-orange-700 hover:bg-orange-100"
              onClick={e => {
                e.stopPropagation();
                onExtendDueDate();
              }}
            >
              <Calendar size={16} />
              Extend Due Date
            </button>
          ) : null}
        </div>

        <div className="flex items-center gap-x-6 gap-y-2 mt-4 text-sm text-gray-600 flex-wrap">
          <MetadataItem
            icon={<Calendar size={16} />}
            label="Period"
            value={group.period}
          />
          <MetadataItem
            icon={<Clock size={16} />}
            label="Due"
            value={formatDiscoveryHubDate(group.due_date)}
          />
          {poll1Enabled && p1?.total != null && (
            <MetadataItem
              icon={<Target size={16} />}
              label="Poll 1"
              value={`${p1.done ?? 0} / ${p1.total}`}
            />
          )}
          {group.poll2_enabled && p2?.total != null && (
            <MetadataItem
              icon={<Target size={16} />}
              label="Poll 2"
              value={`${p2.done ?? 0} / ${p2.total}`}
            />
          )}
          {poll1Enabled ? (
            <MetadataItem
              icon={<Users size={16} />}
              label="Sponsors"
              value={String(sponsorCount)}
            />
          ) : group.poll2_enabled ? (
            <MetadataItem
              icon={<Users size={16} />}
              label="Participants"
              value={String(p2?.total ?? 0)}
            />
          ) : null}
          <MetadataItem
            icon={<Users size={16} />}
            label="Accounts"
            value={String(accountsTotal)}
          />
        </div>
      </div>

      {isExpanded && <div className={DH_POLL_CYCLE_CARD_BODY}>{children}</div>}
    </div>
  );
};

export default PollCycleCard;
